import React, { useState, useEffect, useContext } from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Alert,
  Box,
  Button,
  Container,
  ExpandableSection,
  FormField,
  Input,
  Link,
  Modal,
  RadioGroup,
  StatusIndicator,
  Textarea,
} from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { CopyToClipboard } from "../common/CopyToClipboard";
import { PsnsRedriveApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { generateMcmUrl } from "../../helpers/logUtils";
import { StageContext } from "../../contexes/OrderIdContext";
import {
  PsnsRedriveMcmAttachResult,
  Stage,
} from "../../lusca-search-api/generated-src";
import { StageSelector } from "../redrive/components/StageSelector";
import { FileUploader } from "../redrive/components/FileUploader";
import { usePsnsFileValidation } from "./hooks/usePsnsFileValidation";
import { useExecutePsnsRedrive } from "./hooks/useExecutePsnsRedrive";
import { usePsnsRedriveDetails } from "./hooks/usePsnsRedriveDetails";
import { PsnsRedriveExecutionView } from "./PsnsRedriveExecutionView";
import { PsnsRedriveAttachMcm } from "./PsnsRedriveAttachMcm";
import { getApiErrorMessage } from "./apiErrorMessage";
import {
  PreparedPsnsRedrive,
  clearPreparedRedrive,
  loadPreparedRedrive,
  savePreparedRedrive,
} from "./preparedPsnsRedrive";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

// A real redrive needs an approved MCM only in Prod (the PV Accounting
// validator enforces this; the page just guides the operator).
const MCM_REQUIRED_STAGE: Stage = Stage.Prod;

const pushExecutionArn = (executionArn: string | null) => {
  const urlParams = new URLSearchParams(window.location.search);
  if (executionArn) {
    urlParams.set("executionArn", executionArn);
  } else {
    urlParams.delete("executionArn");
  }
  window.history.pushState(
    {},
    "",
    `${window.location.pathname}?${urlParams.toString()}`
  );
};

export default function PsnsRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const mcmRequired = stage === MCM_REQUIRED_STAGE;
  const [error, setError] = useState<string | null>(null);
  const [mcmError, setMcmError] = useState<string | null>(null);
  const [simLink, setSimLink] = useState<string>("");
  const [mcmContext, setMcmContext] = useState<string>("");
  const [prepared, setPrepared] = useState<PreparedPsnsRedrive | null>(null);
  const [startingDryRun, setStartingDryRun] = useState(false);
  const [startingRedrive, setStartingRedrive] = useState(false);
  const [creatingMcm, setCreatingMcm] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mcmMode, setMcmMode] = useState<"create" | "attach">("create");
  const [selectedExecutionArn, setSelectedExecutionArn] = useState<
    string | null
  >(null);

  // The URL's executionArn picks the view, including after Back/Forward.
  useEffect(() => {
    const syncFromUrl = () =>
      setSelectedExecutionArn(
        new URLSearchParams(window.location.search).get("executionArn")
      );
    syncFromUrl();
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, []);

  const {
    file,
    eventCount,
    error: fileError,
    validating: validatingFile,
    fileOK,
    handleFileChange,
  } = usePsnsFileValidation();
  const { uploadFile, startRedrive } = useExecutePsnsRedrive();
  const {
    details,
    loading: loadingDetails,
    error: detailsError,
  } = usePsnsRedriveDetails(stage, selectedExecutionArn);

  // The prepared upload is stage-specific: restore this stage's (dropping one
  // past its expiry) and forget the previous stage's file and MCM inputs.
  useEffect(() => {
    const restored = loadPreparedRedrive(stage);
    setPrepared(restored);
    setMcmMode(restored?.mcmSource === "attached" ? "attach" : "create");
    setSimLink("");
    setMcmContext("");
    setMcmError(null);
    setError(null);
    handleFileChange([]).catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
  }, [stage, handleFileChange]);

  const runAsyncAction = (action: () => Promise<void>) => {
    action().catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
  };

  const updatePrepared = (next: PreparedPsnsRedrive | null) => {
    setPrepared(next);
    if (next) {
      savePreparedRedrive(stage, next);
    } else {
      clearPreparedRedrive(stage);
    }
  };

  const showExecution = (executionArn: string) => {
    pushExecutionArn(executionArn);
    setSelectedExecutionArn(executionArn);
  };

  // Choosing another file invalidates the prepared upload and any MCM pinned
  // to its S3 URI + ETag.
  const onFileChange = (files: File[]) => {
    updatePrepared(null);
    setMcmError(null);
    setError(null);
    handleFileChange(files).catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
  };

  const handleDiscard = () => {
    updatePrepared(null);
    setSimLink("");
    setMcmContext("");
    setMcmError(null);
    setError(null);
    handleFileChange([]).catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
  };

  // Uploads the selected file once; later steps reuse the same object.
  const ensureUploaded = async (): Promise<PreparedPsnsRedrive> => {
    if (prepared) {
      return prepared;
    }
    if (!file || !fileOK) {
      throw new Error(fileError || "Select a valid NDJSON file first");
    }
    const uploaded = await uploadFile(file, stage);
    if (!uploaded.etag) {
      throw new Error(
        "Upload succeeded but the S3 ETag could not be read from the response"
      );
    }
    const next: PreparedPsnsRedrive = {
      s3Uri: uploaded.s3Uri,
      etag: uploaded.etag,
      count: eventCount,
      savedAt: Date.now(),
    };
    updatePrepared(next);
    return next;
  };

  const handleDryRun = async () => {
    setStartingDryRun(true);
    setError(null);
    try {
      const upload = await ensureUploaded();
      const result = await startRedrive({
        stage,
        eventsS3Uri: upload.s3Uri,
        eventsS3Etag: upload.etag,
        dryRun: true,
      });
      if (!result?.executionArn) {
        throw new Error("The backend did not return an execution ARN");
      }
      updatePrepared({ ...upload, dryRunExecutionArn: result.executionArn });
      showExecution(result.executionArn);
    } catch (err) {
      setError(`Failed to start PSNS dry run: ${getApiErrorMessage(err)}`);
    } finally {
      setStartingDryRun(false);
    }
  };

  const handleCreateMcm = async () => {
    if (!prepared) {
      return;
    }
    setCreatingMcm(true);
    setMcmError(null);
    try {
      const response = await PsnsRedriveApiFactory().postPsnsRedriveMcm(stage, {
        eventsS3Uri: prepared.s3Uri,
        eventsS3Etag: prepared.etag,
        simLink: simLink.trim() || undefined,
        context: mcmContext.trim() || undefined,
      });
      const createdId = response.data?.mcmId;
      if (!createdId) {
        throw new Error("The backend did not return an MCM ID");
      }
      const warning = response.data?.warning;
      updatePrepared({
        ...prepared,
        mcmId: createdId,
        mcmUrl: response.data?.mcmUrl || generateMcmUrl(stage, createdId),
        warning: typeof warning === "string" ? warning : undefined,
        mcmSource: "created",
        mcmUsable: undefined,
      });
    } catch (err) {
      setMcmError(getApiErrorMessage(err));
    } finally {
      setCreatingMcm(false);
    }
  };

  // An attached MCM is kept with its latest check, so a redrive only starts once it passes.
  const handleAttachChecked = (result: PsnsRedriveMcmAttachResult) => {
    if (!prepared) {
      return;
    }
    updatePrepared({
      ...prepared,
      mcmId: result.mcmId,
      mcmUrl: result.mcmUrl,
      warning: undefined,
      mcmSource: "attached",
      mcmUsable: result.usable,
    });
  };

  const handleRedrive = async () => {
    if (!prepared) {
      return;
    }
    setStartingRedrive(true);
    setError(null);
    try {
      const result = await startRedrive({
        stage,
        eventsS3Uri: prepared.s3Uri,
        eventsS3Etag: prepared.etag,
        dryRun: false,
        mcmId: mcmRequired ? prepared.mcmId : undefined,
      });
      if (!result?.executionArn) {
        throw new Error("The backend did not return an execution ARN");
      }
      // The execution keeps its own input, so nothing local is needed anymore.
      handleDiscard();
      showExecution(result.executionArn);
    } catch (err) {
      setError(`Failed to start PSNS redrive: ${getApiErrorMessage(err)}`);
    } finally {
      setStartingRedrive(false);
      setShowConfirm(false);
    }
  };

  const handleBack = () => {
    setSelectedExecutionArn(null);
    pushExecutionArn(null);
  };

  const combinedError =
    [error, detailsError].filter(Boolean).join("; ") || null;

  if (selectedExecutionArn) {
    return (
      <PsnsRedriveExecutionView
        combinedError={combinedError}
        details={details}
        loadingDetails={loadingDetails}
        onBack={handleBack}
        executionArn={selectedExecutionArn}
        stage={stage}
      />
    );
  }

  const preparedCount = prepared?.count ?? eventCount;
  const dryRunDone = !!prepared?.dryRunExecutionArn;
  const mcmCreated =
    prepared?.mcmSource !== "attached" &&
    !!prepared?.mcmId &&
    !!prepared?.mcmUrl;
  // A created MCM's approval is checked by the state machine; an attached one must pass its check.
  const mcmReady =
    !!prepared?.mcmId &&
    (prepared.mcmSource !== "attached" || prepared.mcmUsable === true);
  const canRedrive = dryRunDone && (!mcmRequired || mcmReady);
  const showSteps = !!prepared || (!!file && fileOK);

  return (
    <SpaceBetween size="l">
      {combinedError && (
        <ErrorAlert content={combinedError} header="PSNS Redrive Error" />
      )}

      <Container header={<Box variant="h2">PSNS Redrive Tool</Box>}>
        <SpaceBetween size="m">
          <Box variant="p">
            Republishes PSNS settlement events to the PV Accounting event bus.
            Upload an NDJSON file with one EventBridge envelope per line (up to
            10,000 events), run a dry run to check every event, then start the
            redrive.
            {mcmRequired
              ? " In Prod the redrive needs an approved MCM: create one from this page after the dry run, or use an existing MCM that lists this file."
              : ""}
          </Box>
          <StageSelector stage={stage} setStage={setStage} />
        </SpaceBetween>
      </Container>

      <Container>
        <SpaceBetween size="m">
          {prepared ? (
            <SpaceBetween size="s">
              <Alert type="info" header="Prepared upload">
                {prepared.count} event(s) uploaded on{" "}
                {new Date(prepared.savedAt).toLocaleString()}. Every step below
                uses this file; choose Discard to start over with another file.
              </Alert>
              <ExpandableSection headerText="Uploaded file details">
                <SpaceBetween size="s">
                  <FormField
                    label="S3 URI"
                    secondaryControl={
                      <CopyToClipboard
                        getContent={() => Promise.resolve(prepared.s3Uri)}
                        iconName="copy"
                        ariaLabel="Copy S3 URI"
                      />
                    }
                  >
                    <Input value={prepared.s3Uri} readOnly />
                  </FormField>
                  <FormField label="ETag">
                    <Input value={prepared.etag} readOnly />
                  </FormField>
                </SpaceBetween>
              </ExpandableSection>
            </SpaceBetween>
          ) : (
            <>
              <FileUploader
                file={file}
                handleFileChange={onFileChange}
                redriveOK={false}
                loading={startingDryRun}
                fileError={fileError}
                accept=".ndjson,.jsonl,application/x-ndjson"
                constraintText="NDJSON file, one EventBridge envelope per line (max 10,000)."
                expandedFileToken
              />
              {file && (validatingFile || fileOK) && (
                <div>
                  {validatingFile && (
                    <StatusIndicator type="loading">
                      Validating file...
                    </StatusIndicator>
                  )}
                  {fileOK && (
                    <StatusIndicator type="success">
                      File valid ({eventCount}{" "}
                      {eventCount === 1 ? "event" : "events"})
                    </StatusIndicator>
                  )}
                </div>
              )}
            </>
          )}
        </SpaceBetween>
      </Container>

      {showSteps && (
        <>
          <Container header={<Box variant="h3">Step 1: Dry run</Box>}>
            <SpaceBetween size="m">
              <Box variant="p">
                Validates every event without publishing. Check for INVALID
                events before continuing.
              </Box>
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  onClick={() => runAsyncAction(handleDryRun)}
                  loading={startingDryRun}
                  disabled={startingDryRun || startingRedrive}
                  data-testid="start-dry-run"
                >
                  {dryRunDone ? "Run dry run again" : "Start dry run"}
                </Button>
                {prepared?.dryRunExecutionArn && (
                  <Button
                    variant="link"
                    onClick={() =>
                      showExecution(prepared.dryRunExecutionArn as string)
                    }
                  >
                    View last dry run
                  </Button>
                )}
              </SpaceBetween>
            </SpaceBetween>
          </Container>

          {mcmRequired && (
            <Container header={<Box variant="h3">Step 2: MCM approval</Box>}>
              <SpaceBetween size="m">
                {mcmCreated ? (
                  <SpaceBetween size="s">
                    {prepared?.warning ? (
                      <>
                        <Alert type="success" header="Draft MCM created">
                          <SpaceBetween size="xs">
                            <Box>
                              <Link
                                href={prepared?.mcmUrl}
                                external
                                externalIconAriaLabel="Opens MCM in a new tab"
                              >
                                Open {prepared?.mcmId}
                              </Link>
                            </Box>
                            <Box>
                              In MCM, send it for approval and start it (In
                              Progress), then return here to start the redrive.
                            </Box>
                          </SpaceBetween>
                        </Alert>
                        <Alert type="warning" header="MCM needs attention">
                          {prepared.warning}
                        </Alert>
                      </>
                    ) : (
                      <Alert type="success" header="MCM submitted for approval">
                        <SpaceBetween size="xs">
                          <Box>
                            <Link
                              href={prepared?.mcmUrl}
                              external
                              externalIconAriaLabel="Opens MCM in a new tab"
                            >
                              Open {prepared?.mcmId}
                            </Link>
                          </Box>
                          <Box>
                            pv-track-oncall has been asked to approve it. Once
                            it is approved, start it in MCM (In Progress), then
                            return here to start the redrive.
                          </Box>
                        </SpaceBetween>
                      </Alert>
                    )}
                  </SpaceBetween>
                ) : (
                  <>
                    <RadioGroup
                      value={mcmMode}
                      onChange={({ detail }) =>
                        setMcmMode(detail.value as "create" | "attach")
                      }
                      items={[
                        {
                          value: "create",
                          label: "Create a new MCM",
                          description:
                            "Lusca Search raises the MCM for this file, links the dry run and submits it for approval.",
                        },
                        {
                          value: "attach",
                          label: "Use an existing MCM",
                          description:
                            "An MCM that already lists this file, for example one approved for several files.",
                        },
                      ]}
                    />
                    {mcmMode === "attach" && !prepared && (
                      <Box variant="small" color="text-status-inactive">
                        Run the dry run first. It uploads the file, and the line
                        the MCM must contain depends on it.
                      </Box>
                    )}
                    {mcmMode === "attach" && prepared && (
                      <PsnsRedriveAttachMcm
                        stage={stage}
                        s3Uri={prepared.s3Uri}
                        etag={prepared.etag}
                        initialMcmId={
                          prepared.mcmSource === "attached"
                            ? prepared.mcmId
                            : undefined
                        }
                        disabled={!dryRunDone}
                        onChecked={handleAttachChecked}
                      />
                    )}
                    {mcmMode === "create" && (
                      <>
                        <FormField
                          label="SIM link (optional)"
                          description="Included in the MCM description for traceability."
                        >
                          <Input
                            value={simLink}
                            onChange={({ detail }) => setSimLink(detail.value)}
                            placeholder="https://issues.amazon.com/issues/TRACK-12345"
                          />
                        </FormField>
                        <FormField
                          label="Context (optional)"
                          description="Why these events are being redriven. Shown at the top of the MCM description for the approver."
                        >
                          <Textarea
                            value={mcmContext}
                            onChange={({ detail }) =>
                              setMcmContext(detail.value)
                            }
                            rows={4}
                          />
                        </FormField>
                        <Button
                          onClick={() => runAsyncAction(handleCreateMcm)}
                          loading={creatingMcm}
                          disabled={creatingMcm || !dryRunDone}
                          data-testid="create-draft-mcm"
                        >
                          Create MCM
                        </Button>
                        {!dryRunDone && (
                          <Box variant="small" color="text-status-inactive">
                            Run the dry run first.
                          </Box>
                        )}
                        {mcmError && (
                          <Alert type="error" header="MCM creation failed">
                            {mcmError}
                          </Alert>
                        )}
                      </>
                    )}
                  </>
                )}
              </SpaceBetween>
            </Container>
          )}

          <Container
            header={
              <Box variant="h3">
                {mcmRequired
                  ? "Step 3: Redrive after MCM approval"
                  : "Step 2: Redrive"}
              </Box>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Publishes {preparedCount} event(s) to the PV Accounting event
                bus in {stage}.
                {mcmRequired
                  ? " Confirm the MCM is approved and In Progress first."
                  : ""}
              </Box>
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  variant="primary"
                  onClick={() => setShowConfirm(true)}
                  disabled={!canRedrive || startingRedrive || startingDryRun}
                  loading={startingRedrive}
                  data-testid="start-redrive"
                >
                  Start redrive
                </Button>
                <Button
                  onClick={handleDiscard}
                  disabled={startingRedrive || startingDryRun}
                  data-testid="discard-redrive"
                >
                  Discard
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Container>
        </>
      )}

      {showConfirm && (
        <Modal
          visible={showConfirm}
          onDismiss={() => setShowConfirm(false)}
          header="Start PSNS redrive"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => setShowConfirm(false)}
                  disabled={startingRedrive}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => runAsyncAction(handleRedrive)}
                  loading={startingRedrive}
                  data-testid="confirm-redrive"
                >
                  Publish events
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          This publishes {preparedCount} event(s) to the PV Accounting event bus
          in <b>{stage}</b>. Published events are processed by accounting and
          cannot be recalled.
        </Modal>
      )}
    </SpaceBetween>
  );
}
